import { useState } from 'react';
import { criarUsuario, atualizarUsuario } from '../../api/usuarios.js';
import type { UsuarioDoPainel } from '../../api/usuarios.js';
import { mensagemDoErro } from '../../api/erros.js';
import { PERFIS } from '../../autorizacao/permissoes.js';
import { CampoSelecao, CampoTexto } from '../../componentes/Campo.js';
import { FormularioLateral } from '../../componentes/FormularioLateral.js';
import { seHouver } from '../../componentes/props.js';
import type { PerfilUsuario } from '../../autenticacao/tipos.js';

/** Nunca se altera o próprio perfil nem se desativa (§4 do contrato). */
const AVISO_PROPRIO = 'Ninguém altera o próprio perfil nem se desativa.';

const OPCOES_DE_PERFIL = PERFIS.map((perfil) => ({ valor: perfil, rotulo: perfil }));

type Props = {
  /** `null` cadastra; com usuário, edita. */
  readonly usuario: UsuarioDoPainel | null;
  readonly ehProprio: boolean;
  readonly aoFechar: () => void;
  readonly aoConcluir: (mensagem: string) => void;
};

function ehPerfil(valor: string): valor is PerfilUsuario {
  return (PERFIS as readonly string[]).includes(valor);
}

export function FormularioDeUsuario({ usuario, ehProprio, aoFechar, aoConcluir }: Props) {
  const [login, definirLogin] = useState(usuario?.usuario ?? '');
  const [nome, definirNome] = useState(usuario?.nome ?? '');
  const [senha, definirSenha] = useState('');
  const [perfil, definirPerfil] = useState<PerfilUsuario>(usuario?.perfil ?? 'Consulta');
  const [erros, definirErros] = useState<Readonly<Record<string, string>>>({});
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  const editando = usuario !== null;

  function validar(): Readonly<Record<string, string>> {
    const encontrados: Record<string, string> = {};

    if (!editando && login.trim() === '') {
      encontrados['login'] = 'Informe o login.';
    }
    if (nome.trim() === '') {
      encontrados['nome'] = 'Informe o nome.';
    }
    if (!editando && senha === '') {
      encontrados['senha'] = 'Informe a senha inicial.';
    }

    return encontrados;
  }

  function confirmar() {
    const encontrados = validar();
    definirErros(encontrados);

    if (Object.keys(encontrados).length > 0) {
      return;
    }

    definirErro(null);
    definirEnviando(true);

    const escrita = editando
      ? // PATCH só com o que a tela edita — quem não manda o campo não o zera.
        atualizarUsuario(
          usuario.id,
          ehProprio ? { nome: nome.trim() } : { nome: nome.trim(), perfil },
        )
      : criarUsuario({ usuario: login.trim(), nome: nome.trim(), senha, perfil });

    escrita
      .then((salvo) => {
        aoConcluir(
          editando
            ? `Usuário ${salvo.usuario} atualizado.`
            : `Usuário ${salvo.usuario} cadastrado.`,
        );
      })
      .catch((falha: unknown) => {
        definirEnviando(false);
        definirErro(mensagemDoErro(falha));
      });
  }

  return (
    <FormularioLateral
      titulo={editando ? `Editar ${usuario.usuario}` : 'Novo usuário do painel'}
      subtitulo={
        editando
          ? 'Usuário nunca é apagado — a trilha de auditoria referencia o autor.'
          : 'Quem entra pelo navegador. O operador do coletor é cadastrado na outra aba.'
      }
      rotuloConfirmar={editando ? 'Salvar' : 'Cadastrar'}
      aoConfirmar={confirmar}
      aoFechar={aoFechar}
      enviando={enviando}
      {...seHouver('erro', erro)}
    >
      <CampoTexto
        rotulo="Login"
        valor={login}
        aoMudar={definirLogin}
        fonte="mono"
        obrigatorio={!editando}
        desabilitado={editando}
        autoCompletar="off"
        {...seHouver(
          'ajuda',
          editando ? 'O login não muda: ele identifica o autor na auditoria.' : null,
        )}
        {...seHouver('erro', erros['login'])}
      />
      <CampoTexto
        rotulo="Nome"
        valor={nome}
        aoMudar={definirNome}
        obrigatorio
        autoCompletar="off"
        {...seHouver('erro', erros['nome'])}
      />
      {!editando && (
        <CampoTexto
          rotulo="Senha"
          valor={senha}
          aoMudar={definirSenha}
          tipo="password"
          obrigatorio
          autoCompletar="new-password"
          ajuda="A pessoa troca a senha depois, informando esta como senha atual."
          {...seHouver('erro', erros['senha'])}
        />
      )}
      <CampoSelecao
        rotulo="Perfil"
        valor={perfil}
        opcoes={OPCOES_DE_PERFIL}
        desabilitado={ehProprio}
        ajuda={ehProprio ? AVISO_PROPRIO : 'A matriz abaixo da tabela diz o que cada perfil faz.'}
        aoMudar={(valor) => {
          if (ehPerfil(valor)) {
            definirPerfil(valor);
          }
        }}
      />
    </FormularioLateral>
  );
}
